import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/components/Footer";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function BlogList() {
  const { t, lang } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, [lang]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/posts?language=${lang}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-US', options);
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t('blog.title')} | Chatity</title>
        <meta name="description" content={t('blog.subtitle')} />
        <meta property="og:title" content={`${t('blog.title')} | Chatity`} />
        <meta property="og:description" content={t('blog.subtitle')} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://chatity.io/blog" />
        
        {/* Hreflang tags for blog list page */}
        <link rel="alternate" hreflang="en" href="https://chatity.io/blog?lang=en" />
        <link rel="alternate" hreflang="fr" href="https://chatity.io/blog?lang=fr" />
        <link rel="alternate" hreflang="ru" href="https://chatity.io/blog?lang=ru" />
        <link rel="alternate" hreflang="x-default" href="https://chatity.io/blog" />
      </Helmet>

      <div className="min-h-screen bg-white text-black">
        {/* Header */}
        <header className="border-b-2 border-black">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-header.png" alt="Chatity" className="h-8 w-auto" />
          </Link>
          
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/auth">
              <button className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium">
                {t('landing.login')}
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Link to="/" className="hover:text-black">{t('landing.home')}</Link>
          <span>/</span>
          <span className="text-black font-medium">{t('blog.title')}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 md:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            {t('blog.title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600">
            {t('blog.subtitle')}
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="container mx-auto px-4 pb-20">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600">{t('blog.noPosts')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.slug}`}
                className="group"
              >
                <article className="border-2 border-black rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  {/* Featured Image */}
                  {post.featured_image ? (
                    <div className="aspect-[12/5] bg-gray-100 overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        title={post.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[12/5] bg-gradient-to-br from-black to-gray-800 flex items-center justify-center">
                      <div className="text-white text-6xl font-bold opacity-20">C</div>
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h2 className="text-2xl font-bold mb-3 group-hover:text-gray-700 transition-colors">
                      {post.title}
                    </h2>
                    
                    {post.excerpt && (
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}
                    
                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-auto pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.published_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{calculateReadTime(post.content)} min</span>
                      </div>
                    </div>
                    
                    {/* Read More */}
                    <div className="flex items-center gap-2 text-black font-medium mt-4 group-hover:gap-3 transition-all">
                      <span>{t('blog.readMore')}</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </section>

        <Footer />
      </div>
    </>
  );
}
